import { StringContext } from "../../core/StringContext";
import { StringModule } from "../../core/StringModule";
import { StringObject } from "../../objects/StringObject";

/**
 * Module that handles lazy-loading of images with `string-lazy` attribute.
 * It loads the image only once, calculates its aspect-ratio to prevent layout shift,
 * and then displays it.
 */
export class StringLazy extends StringModule {
  private isStartLoaded = false;
  private loadingCount = 0;

  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "lazy";
  }

  onInit(): void {
    const images = document.querySelectorAll("img[string-lazy], img[data-string-lazy]");
    images.forEach((img) => this.loadImage(img as HTMLImageElement));
    this.isStartLoaded = true;
  }

  onObjectConnected(object: StringObject): void {
    if (this.isStartLoaded) {
      const img = object.htmlElement as HTMLImageElement;
      this.loadImage(img);
    }
  }

  private loadImage(img: HTMLImageElement): void {
    if (img.dataset.lazyStatus === "loading" || img.dataset.lazyStatus === "loaded") {
      return;
    }

    const src = this.tools.domAttribute.process({
      element: img,
      key: this.htmlKey,
      fallback: "",
    });

    if (!src) return;
    img.dataset.lazyStatus = "loading";
    img.classList.add("lazyLoad");
    this.loadingCount++;
    const preloader = new Image();
    preloader.onload = () => {
      img.style.aspectRatio = `${preloader.width} / ${preloader.height}`;
      img.src = src;
      img.classList.add("-loaded");
      img.dataset.lazyStatus = "loaded";

      this.handleLoadingCompletion();
    };
    preloader.onerror = () => {
      console.warn("Failed to load image:", src);
      img.dataset.lazyStatus = "error";
      this.handleLoadingCompletion();
    };
    preloader.src = src;
  }

  private handleLoadingCompletion(): void {
    this.loadingCount--;
    if (this.loadingCount <= 0) {
      this.events.emit("image:load:all", null);
      this.loadingCount = 0;
    }
  }
}
